/**
 * @file useReaderVisualViewport.js
 * @description 阅读页视觉视口同步：捏合缩放时将 chrome 锚定到当前可见区域
 * @module features/comic/composables/useReaderVisualViewport
 */

import { onBeforeUnmount, onMounted } from 'vue'

/** 阅读页激活时挂到 documentElement 的类名 */
export const READER_VV_ROOT_CLASS = 'comic-reader-vv-active'

/** 视觉视口 CSS 变量名 */
export const READER_VV_VARS = {
  top: '--reader-vv-top',
  left: '--reader-vv-left',
  width: '--reader-vv-width',
  height: '--reader-vv-height',
  scale: '--reader-vv-scale',
  inverseScale: '--reader-vv-inverse-scale',
}

/**
 * @param {number} scale visualViewport.scale，无效时视为 1
 * @returns {number}
 */
function normalizeViewportScale(scale) {
  return typeof scale === 'number' && scale > 0 ? scale : 1
}

/**
 * 将 visualViewport 几何信息写入 documentElement CSS 变量
 */
export function syncReaderVisualViewport() {
  const root = document.documentElement
  const vv = window.visualViewport

  if (!vv) {
    root.style.setProperty(READER_VV_VARS.top, '0px')
    root.style.setProperty(READER_VV_VARS.left, '0px')
    root.style.setProperty(READER_VV_VARS.width, '100vw')
    root.style.setProperty(READER_VV_VARS.height, '100vh')
    root.style.setProperty(READER_VV_VARS.scale, '1')
    root.style.setProperty(READER_VV_VARS.inverseScale, '1')
    return
  }

  const scale = normalizeViewportScale(vv.scale)

  root.style.setProperty(READER_VV_VARS.top, `${vv.offsetTop}px`)
  root.style.setProperty(READER_VV_VARS.left, `${vv.offsetLeft}px`)
  root.style.setProperty(READER_VV_VARS.width, `${vv.width}px`)
  root.style.setProperty(READER_VV_VARS.height, `${vv.height}px`)
  root.style.setProperty(READER_VV_VARS.scale, String(scale))
  root.style.setProperty(READER_VV_VARS.inverseScale, String(1 / scale))
}

/**
 * 清除阅读页视觉视口 CSS 变量与根类名
 */
export function clearReaderVisualViewport() {
  const root = document.documentElement
  root.classList.remove(READER_VV_ROOT_CLASS)
  Object.values(READER_VV_VARS).forEach((name) => {
    root.style.removeProperty(name)
  })
}

/**
 * 阅读页挂载期间监听 visualViewport，使固定控件跟随可见区域
 */
export function useReaderVisualViewport() {
  const handleVisualViewportChange = () => {
    syncReaderVisualViewport()
  }

  onMounted(() => {
    document.documentElement.classList.add(READER_VV_ROOT_CLASS)
    syncReaderVisualViewport()

    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', handleVisualViewportChange)
      vv.addEventListener('scroll', handleVisualViewportChange)
    }
    window.addEventListener('resize', handleVisualViewportChange)
  })

  onBeforeUnmount(() => {
    const vv = window.visualViewport
    if (vv) {
      vv.removeEventListener('resize', handleVisualViewportChange)
      vv.removeEventListener('scroll', handleVisualViewportChange)
    }
    window.removeEventListener('resize', handleVisualViewportChange)
    clearReaderVisualViewport()
  })
}
